import { Dispatch, SetStateAction } from 'react'

import { useAccount } from 'wagmi'

import { BigNumberInput } from '@/common/components/Input/BigNumberInput'
import { CoinMark } from '@/design-system/CoinMark'
import { displayBN } from '@/utils/numberFormatter'

export interface StakeFormProps {
  amount: bigint
  setAmount: Dispatch<SetStateAction<bigint>>
  setReward?: Dispatch<SetStateAction<bigint>>
  balance: bigint
  greaterThanPool?: boolean
  token?: string
  header?: string
  max?: bigint
  toggle?: 'stake' | 'unstake'
}

function TokenMark({ token }: { token: string }) {
  // The underlying coin's monochrome isotipo (black disc + white mark), matching the vault cards.
  return (
    <span className="flex flex-none items-center gap-2">
      <CoinMark size={26} token={token} />
      <span style={{ color: 'var(--secondary)', fontSize: 14, fontWeight: 400 }}>{token}</span>
    </span>
  )
}

export const StakeForm = ({
  amount,
  balance,
  greaterThanPool,
  header,
  max,
  setAmount,
  token = 'AVAX',
  toggle,
}: StakeFormProps): JSX.Element => {
  const { isConnected } = useAccount()

  // The spendable base: on withdraw it's the full balance; on deposit we hold back a 0.15 gas
  // buffer (native AVAX). Capped by `max` (deposit cap / redeemable pool) when present. This is the
  // exact value the old "Max" used — presets take fractions of it.
  const gasBuffer = BigInt(0.15 * 10 ** 18)
  const spendableBase =
    toggle === 'unstake' ? balance : balance > gasBuffer ? balance - gasBuffer : 0n
  const available = max != null && max < spendableBase ? max : spendableBase

  const setFraction = (pct: number) => {
    setAmount((available * BigInt(pct)) / 100n)
  }

  return (
    <>
      <div className="rng-label" style={{ marginBottom: 12 }}>
        {header || 'Amount'}
      </div>

      <div className="rng-field flex items-center gap-3" style={{ border: 'none' }}>
        <BigNumberInput
          autoFocus
          bnValue={amount}
          className="rng-input min-w-0 flex-1 disabled:cursor-not-allowed"
          disabled={!isConnected}
          min={0n}
          onChange={(value) => setAmount(value)}
          style={{ color: greaterThanPool ? 'var(--crit)' : undefined }}
        />
        <TokenMark token={token} />
      </div>

      <div className="mt-3 flex items-center gap-2" style={{ color: 'var(--muted)', fontSize: 13 }}>
        <span className="truncate">
          {toggle === 'unstake' ? 'Withdrawable' : 'In wallet'} {displayBN(balance)} {token}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2">
        <button
          className="rng-max"
          disabled={!isConnected}
          onClick={() => setFraction(25)}
          type="button"
        >
          25%
        </button>
        <button
          className="rng-max"
          disabled={!isConnected}
          onClick={() => setFraction(50)}
          type="button"
        >
          50%
        </button>
        <button
          className="rng-max"
          disabled={!isConnected}
          onClick={() => setFraction(100)}
          type="button"
        >
          Max
        </button>
      </div>
    </>
  )
}
